import React, { useEffect, useRef, useState } from "react";
import { Users, Minus, Plus, ShoppingCart, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_MEAL = "griot_pikliz_plantains";
// Minimum delay between consecutive add-to-cart clicks on the same family
// plate. A real human can't legitimately add the same plate twice in <2s,
// so anything faster is treated as an accidental double/multi-tap.
const CLICK_GUARD_MS = 2500;

export default function FamilyMeals({ config, meals, onAdd, showcaseMode = false, onCtaToMenu }) {
    const [members, setMembers] = useState(config?.min_members ?? 4);
    const [selectedId, setSelectedId] = useState(DEFAULT_MEAL);
    const [adding, setAdding] = useState(false);
    const lastClickAt = useRef(0);

    useEffect(() => {
        if (meals?.length && !meals.find((m) => m.id === selectedId)) {
            setSelectedId(meals[0].id);
        }
    }, [meals, selectedId]);

    // Re-sync members when config arrives (fallback → real API).
    useEffect(() => {
        if (typeof config?.min_members === "number") {
            setMembers(config.min_members);
        }
    }, [config?.min_members]);

    if (!config || !meals?.length) return null;

    const min = config.min_members ?? 4;
    const max = config.max_members ?? 15;
    const meal = meals.find((m) => m.id === selectedId) || meals[0];
    // Family plate ignores premium meals — only regular menu items can be plated family-style.
    const familyEligibleMeals = meals.filter((m) => !m.is_premium);
    const unitPrice = +(meal.price * (members - 1)).toFixed(2);

    const add = () => {
        if (showcaseMode) {
            if (typeof onCtaToMenu === "function") onCtaToMenu();
            return;
        }
        // Hard guard against accidental rapid double/multi-tap (mobile touch
        // events can fire onClick 2–4× in quick succession). If the customer
        // really wants two plates they can click again after the cooldown.
        const now = Date.now();
        if (adding || now - lastClickAt.current < CLICK_GUARD_MS) {
            return;
        }
        lastClickAt.current = now;
        // Safety: family plates can be expensive (e.g. 4 people = price × 3).
        const confirmMsg = `Add a family-size ${meal.name} for ${members} people at $${unitPrice.toFixed(2)}?\n\nThis is one large family plate that serves ${members} (not ${members} separate meals).`;
        if (typeof window !== "undefined" && !window.confirm(confirmMsg)) {
            return;
        }
        setAdding(true);
        onAdd({ meal_id: meal.id, meal_name: meal.name, members, price: unitPrice });
        toast.success(
            `Added: Family ${meal.name} · serves ${members} · $${unitPrice.toFixed(2)}`
        );
        // Visual cooldown so the button can't be re-fired in <2.5s
        setTimeout(() => setAdding(false), CLICK_GUARD_MS);
    };

    return (
        <section
            id="family-meals"
            data-testid="family-meals-section"
            className="bg-cream pb-20 md:pb-28 px-6 sm:px-8"
        >
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
                {/* Image — purely decorative, NOT a button. Customers must use the
                    explicit "Add Family Plate to Order" CTA below. Tapping the
                    image previously added a 4-person plate by accident. */}
                <div
                    data-testid="family-image"
                    className="relative reveal rounded-[24px] overflow-hidden lg:sticky lg:top-24"
                >
                    <div className="absolute -inset-3 bg-gold/20 rounded-[28px] blur-2xl pointer-events-none" />
                    <img
                        src={config.image}
                        alt="Caribbean Meal Prep — Family-Sized Meals"
                        className="relative rounded-[24px] shadow-[0_18px_50px_rgba(13,40,24,0.25)] w-full h-auto"
                        draggable={false}
                    />
                    <span className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-gold text-navy text-[11px] font-bold tracking-widest uppercase shadow-md">
                        Family-Sized
                    </span>
                </div>

                {/* Configurator */}
                <div className="reveal" style={{ transitionDelay: "120ms" }}>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-burnt/10 border border-burnt/30 text-burnt text-[11px] font-bold tracking-widest uppercase">
                        <Users size={12} /> Family-Sized
                    </span>
                    <h2 className="mt-4 font-serif font-black text-3xl sm:text-4xl md:text-5xl leading-tight text-navy">
                        Bring everyone to the{" "}
                        <span className="text-forest italic">table</span>.
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-muted2 leading-relaxed max-w-lg">
                        <span className="font-bold text-navy">One large plate, shared by everyone at the table.</span>{" "}
                        Pick a dish, tell us how many people are eating, and we'll
                        plate it family-style. You only pay for{" "}
                        <span className="font-bold text-navy">(people − 1)</span> —
                        so feeding 4 costs the same as 3 single plates.{" "}
                        <span className="block mt-2 text-burnt font-bold">
                            Heads up: this is ONE shareable plate, not multiple separate meals.
                        </span>
                    </p>

                    {/* Meal picker */}
                    <div className="mt-6">
                        <p className="text-[11px] uppercase tracking-widest font-bold text-muted2 mb-2.5">
                            1 · Pick a dish
                        </p>
                        <div
                            data-testid="family-meal-picker"
                            className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                        >
                            {familyEligibleMeals.map((m) => {
                                const active = m.id === selectedId;
                                return (
                                    <button
                                        type="button"
                                        key={m.id}
                                        onClick={() => setSelectedId(m.id)}
                                        data-testid={`family-meal-option-${m.id}`}
                                        className={`group relative flex items-center gap-3 p-2.5 rounded-2xl border-2 transition-all text-left ${
                                            active
                                                ? "border-forest bg-mint/40"
                                                : "border-mint bg-white hover:border-sage"
                                        }`}
                                    >
                                        <img
                                            src={m.image}
                                            alt=""
                                            className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-navy text-sm truncate">
                                                {m.name}
                                            </p>
                                            <p className="text-[11px] text-muted2">
                                                ${m.price.toFixed(2)} each
                                            </p>
                                        </div>
                                        {active && (
                                            <span className="w-6 h-6 rounded-full bg-forest text-white flex items-center justify-center flex-shrink-0">
                                                <Check size={14} />
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Stepper */}
                    <div className="mt-6 bg-white rounded-[20px] p-5 border-2 border-mint shadow-[0_8px_28px_rgba(0,0,0,0.07)]">
                        <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                            2 · How many people?
                        </p>

                        <div className="mt-4 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 bg-cream rounded-full p-1.5 border border-mint">
                                <button
                                    data-testid="family-members-decrement"
                                    onClick={() =>
                                        setMembers((m) => Math.max(min, m - 1))
                                    }
                                    disabled={members <= min}
                                    aria-label="Decrease family size"
                                    className="w-11 h-11 rounded-full flex items-center justify-center text-forest hover:bg-mint disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    <Minus size={18} />
                                </button>
                                <div
                                    data-testid="family-members-count"
                                    className="font-display text-3xl text-navy w-14 text-center tabular-nums"
                                >
                                    {members}
                                </div>
                                <button
                                    data-testid="family-members-increment"
                                    onClick={() =>
                                        setMembers((m) => Math.min(max, m + 1))
                                    }
                                    disabled={members >= max}
                                    aria-label="Increase family size"
                                    className="w-11 h-11 rounded-full flex items-center justify-center bg-forest text-white hover:bg-sage disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
                                >
                                    <Plus size={18} />
                                </button>
                            </div>
                            <div className="text-right">
                                <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                                    Total
                                </p>
                                <p
                                    data-testid="family-price"
                                    className="font-display text-4xl sm:text-5xl text-forest tabular-nums leading-none"
                                >
                                    ${unitPrice.toFixed(2)}
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-mint text-xs text-muted2 leading-relaxed">
                            <span className="font-bold text-navy">
                                {meal.name}
                            </span>{" "}
                            · ${meal.price.toFixed(2)} ×{" "}
                            <span className="font-bold text-forest">
                                {members - 1}
                            </span>{" "}
                            ={" "}
                            <span className="font-bold text-forest">
                                ${unitPrice.toFixed(2)}
                            </span>
                        </div>

                        <button
                            data-testid="family-add-btn"
                            onClick={add}
                            disabled={adding}
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gold hover:bg-goldDark text-navy font-bold transition-all active:scale-[0.99] shadow-[0_10px_28px_rgba(244,164,53,0.35)] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {adding ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Added — give it a sec…
                                </>
                            ) : showcaseMode ? (
                                <>
                                    <ShoppingCart size={16} />
                                    Order on the Full Menu
                                </>
                            ) : (
                                <>
                                    <ShoppingCart size={16} />
                                    Add Family Plate to Order
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
